import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { GoogleMap } from "@jambushrushti/components/shared/GoogleMap";
import { FeedbackQRCode } from "@jambushrushti/components/shared/FeedbackQRCode";
import { Mail, MapPin, Phone, Send, Clock } from "lucide-react";

const contactInfo = [
  {
    icon: MapPin,
    title: "Address",
    content:
      "Muni Jambuvijayaji Gyan Mandir\nNear Adani Shantigram Jain Temple\nAdani Shantigram Township\nAhmedabad 382 421",
  },
  {
    icon: Mail,
    title: "Email",
    content: "contact@jambushrusti.org",
  },
  {
    icon: Phone,
    title: "Phone",
    content: "+91-98250 44911",
  },
  {
    icon: Clock,
    title: "Hours",
    content:
      "Mon - Sat: 10:00 AM - 6:00 PM IST\nVisiting: 10:45 AM - 5:15 PM IST",
  },
];

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiryType: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));

    toast({
      title: "Message Sent",
      description:
        "Thank you for contacting us. We'll respond within 2-3 business days.",
    });

    setFormData({
      name: "",
      email: "",
      inquiryType: "",
      subject: "",
      message: "",
    });
    setIsSubmitting(false);
  };

  return (
    <Layout>
      <Seo
        title="Contact Us"
        description="Get in touch with Muni Jambuvijayaji Research Center. Contact us for research inquiries, library access, or questions about Gurudev Muni Jambuvijayaji Maharaj Saheb's teachings."
      />
      {/* Hero */}
      <section className="bg-gradient-hero lotus-pattern py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-heading animate-fade-up mb-6 text-4xl font-bold text-foreground sm:text-5xl">
              Contact Us
            </h1>
            <p className="font-body animate-fade-up text-muted-foreground delay-100">
              Have questions about our research, library, or programs? We'd love
              to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-background py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-3">
            {/* Contact Info */}
            <div className="space-y-6">
              <h2 className="font-heading mb-6 text-2xl font-bold text-foreground">
                Get in Touch
              </h2>
              {contactInfo.map((item, index) => (
                <div
                  key={item.title}
                  className="animate-fade-up flex items-start gap-4"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-medium text-foreground">
                      {item.title}
                    </h3>
                    <p className="font-body whitespace-pre-line text-muted-foreground">
                      {item.content}
                    </p>
                  </div>
                </div>
              ))}

              {/* Feedback QR Code */}
              <div className="pt-4">
                <FeedbackQRCode compact />
              </div>
            </div>

            {/* Contact Form */}
            <Card
              variant="elevated"
              className="animate-fade-up delay-200 lg:col-span-2"
            >
              <CardHeader>
                <CardTitle>Send a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="inquiryType">Inquiry Type</Label>
                      <Select
                        value={formData.inquiryType}
                        onValueChange={value =>
                          setFormData(prev => ({ ...prev, inquiryType: value }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">
                            General Inquiry
                          </SelectItem>
                          <SelectItem value="scholar">
                            Scholar Access
                          </SelectItem>
                          <SelectItem value="library">Library Visit</SelectItem>
                          <SelectItem value="research">
                            Research Collaboration
                          </SelectItem>
                          <SelectItem value="technical">
                            Technical Support
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        required
                        value={formData.subject}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            subject: e.target.value,
                          }))
                        }
                        placeholder="Subject of your message"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      placeholder="How can we help you?"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="hero"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-secondary/20 py-16">
        <div className="container mx-auto px-4">
          <div className="mb-10 text-center">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground lg:text-3xl">
              Find Us
            </h2>
            <p className="mx-auto max-w-xl text-muted-foreground">
              Visit our library and research center located near Adani
              Shantigram Jain Temple
            </p>
          </div>
          <div className="mx-auto max-w-5xl">
            <GoogleMap height="450px" />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
